<script lang="ts" setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useEvents } from '@/plugins/events';
import { useAlerts } from '@/plugins/alerts';
import { Form } from '@/helpers/Form';
import { $t } from '@/plugins/localization';
import { BpmnEmitOptions } from '@/modules/low-code/process/services/BpmnService';
import { FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import ProcessService, {
    DocumentParticipantsModel,
    RemoveParticipantsModel,
} from '@/modules/low-code/process/services/ProcessService';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';

const { $events } = useEvents();
const { $alert } = useAlerts();

const modal = ref<InstanceType<typeof IdeoModal> | null>(null);

const documentParticipants = ref<DocumentParticipantsModel>({
    userParticipants: [],
    unitParticipants: [],
    teamParticipants: [],
    groupParticipants: [],
});

const form = reactive(
    Form.create<RemoveParticipantsModel>({
        userParticipants: [],
        unitParticipants: [],
        teamParticipants: [],
        groupParticipants: [],
    })
);

const action = reactive(
    Form.create<BpmnEmitOptions>({
        id: '',
        licence: '',
        actionName: '',
        buttonId: '',
        displayMode: 'Modal',
        displaySize: 'Medium',
        title: '',
        buttonName: $t('[[[Odbierz uprawnienia do dokumentu]]]'),
        buttonVariant: 'success',
        buttonIcon: '',
        groupAction: false,
    })
);

const userOptions = computed(() =>
    documentParticipants.value.userParticipants.map((p) => ({ value: p.key, text: p.value }))
);
const unitOptions = computed(() =>
    documentParticipants.value.unitParticipants.map((p) => ({ value: p.key, text: p.value }))
);
const teamOptions = computed(() =>
    documentParticipants.value.teamParticipants.map((p) => ({ value: p.key, text: p.value }))
);
const groupOptions = computed(() =>
    documentParticipants.value.groupParticipants.map((p) => ({ value: p.key, text: p.value }))
);

const isDataEmpty = computed(
    () =>
        userOptions.value.length === 0 &&
        unitOptions.value.length === 0 &&
        teamOptions.value.length === 0 &&
        groupOptions.value.length === 0
);

const loadData = async () =>
{
    try
    {
        const response = await ProcessService.getDocumentParticipants(action.licence, action.id);

        documentParticipants.value = response;
    }
    catch (ex: any)
    {
        $alert.warning(ex.message);
    }
};

const onSubmit = async () =>
{
    try
    {
        form.wait();

        await ProcessService.removeDocumentParticipants(action.licence, action.id, form.formatData());
        $alert.success($t('[[[Akcja wykonana pomyślnie]]]'));
        modal.value?.hide();

        setTimeout(() =>
        {
            $events.$emit('refetchData');
            $events.$emit('refetchActions');
            form.reset();
        }, 500);
        form.clear();
    }
    catch (ex: any)
    {
        if (ex.code === 400)
        {
            $alert.warning(ex.message);
        }
        else if (ex.code === 422)
        {
            form.$errors.record(ex.data.errors);
            $alert.warning($t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]'));
        }
    }
    finally
    {
        form.continue();
    }
};

onMounted(async () =>
{
    $events.$on(FrontActionModal.RemoveParticipantsModal, async (_action: BpmnEmitOptions) =>
    {
        action.withData(_action);

        if (!action.id) return;

        await loadData();
        modal.value?.show();
    });
});

onUnmounted(() =>
{
    $events.$off(FrontActionModal.RemoveParticipantsModal);
});
</script>

<template>
    <IdeoModal ref="modal" :title="action.title" centered>
        <template #default>
            <IdeoForm id="remove-participants-form">
                <IdeoFormGroup v-if="userOptions.length > 0" :label="$t('[[[Użytkownicy]]]')">
                    <IdeoFormGroup
                        v-for="(option, index) in userOptions"
                        :key="option.value"
                        :state="form.$errors.state(`userParticipants[${index}]`)"
                        nospace
                    >
                        <IdeoFormCheckbox v-model="form.userParticipants" :value="option.value">
                            {{ option.text }}
                        </IdeoFormCheckbox>
                    </IdeoFormGroup>
                </IdeoFormGroup>
                <IdeoFormGroup v-if="unitOptions.length > 0" :label="$t('[[[Działy]]]')">
                    <IdeoFormGroup
                        v-for="(option, index) in unitOptions"
                        :key="option.value"
                        :invalid-feedback="form.$errors.first(`unitParticipants[${index}]`)"
                        :state="form.$errors.state(`unitParticipants[${index}]`)"
                        nospace
                    >
                        <IdeoFormCheckbox v-model="form.unitParticipants" :value="option.value">
                            {{ option.text }}
                        </IdeoFormCheckbox>
                    </IdeoFormGroup>
                </IdeoFormGroup>
                <IdeoFormGroup v-if="teamOptions.length > 0" :label="$t('[[[Zespoły]]]')">
                    <IdeoFormGroup
                        v-for="(option, index) in teamOptions"
                        :key="option.value"
                        :invalid-feedback="form.$errors.first(`teamParticipants[${index}]`)"
                        :state="form.$errors.state(`teamParticipants[${index}]`)"
                        nospace
                    >
                        <IdeoFormCheckbox v-model="form.teamParticipants" :value="option.value">
                            {{ option.text }}
                        </IdeoFormCheckbox>
                    </IdeoFormGroup>
                </IdeoFormGroup>
                <IdeoFormGroup v-if="groupOptions.length > 0" :label="$t('[[[Grupy]]]')">
                    <IdeoFormGroup
                        v-for="(option, index) in groupOptions"
                        :key="option.value"
                        :invalid-feedback="form.$errors.first(`groupParticipants[${index}]`)"
                        :state="form.$errors.state(`groupParticipants[${index}]`)"
                        nospace
                    >
                        <IdeoFormCheckbox v-model="form.groupParticipants" :value="option.value">
                            {{ option.text }}
                        </IdeoFormCheckbox>
                    </IdeoFormGroup>
                </IdeoFormGroup>
                <div v-if="isDataEmpty" class="alert alert-info text-center mb-0">
                    {{ $t('[[[Nie ma użytkowników, którym można odebrać uprawnienia do dokumentu]]]') }}
                </div>
            </IdeoForm>
            <div v-if="form.$errors.first('userParticipants[0]')" class="errors-wrapper">
                <small class="form-text text-danger">
                    {{ form.$errors.first('userParticipants[0]') }}
                </small>
            </div>
        </template>
        <template #modal-footer="{ cancel }">
            <ActionButton
                v-if="!isDataEmpty"
                name="remove-participants-form"
                submit
                variant="success"
                icon="fas fa-check"
                :text="$t('[[[Zapisz]]]')"
                :confirm="
                    $t(
                        '[[[“Po potwierdzeniu wybrani użytkownicy całkowicie utracą dostęp do dokumentu. Czy chcesz kontynuować?]]]'
                    )
                "
                @click="onSubmit"
            />
            <IdeoButton variant="secondary" @click="cancel">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
